import { useEffect, useRef } from 'react'

const FOCUSABLE_SELECTORS = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

/**
 * Traps focus inside containerRef while isOpen is true.
 * Focuses first focusable element on open.
 * Restores focus to triggerRef (or previous active element) on close.
 */
export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  isOpen:       boolean,
  triggerRef?:  React.RefObject<HTMLElement | null>
): void {
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Save + focus on open
  useEffect(() => {
    if (!isOpen || !containerRef.current) return
    previousFocusRef.current = (triggerRef?.current ?? document.activeElement) as HTMLElement
    const focusable = Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
    ).filter((el) => !el.closest('[aria-hidden="true"]'))
    if (focusable.length > 0) {
      const timer = setTimeout(() => focusable[0].focus(), 50)
      return () => clearTimeout(timer)
    }
  }, [isOpen, containerRef, triggerRef])

  // Restore focus on close — Rule 7
  useEffect(() => {
    if (!isOpen) {
      const prev = previousFocusRef.current
      if (prev && document.contains(prev)) {
        const timer = setTimeout(() => prev.focus(), 50)
        return () => clearTimeout(timer)
      }
    }
  }, [isOpen])

  // Tab trap
  useEffect(() => {
    if (!isOpen || !containerRef.current) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const focusable = Array.from(
        containerRef.current!.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
      ).filter((el) => !el.closest('[aria-hidden="true"]'))
      if (focusable.length === 0) return
      const first = focusable[0]
      const last  = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, containerRef])
}
